import React, { useState, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
  FloatingFocusManager,
  useDismiss,
  useRole,
  useListNavigation,
  useInteractions,
  useClick,
} from '@floating-ui/react';

/* Shared dark-themed select built on Floating UI — replaces the native
   select control whose popup ignores `color-scheme: dark` on Windows
   Chrome (white-on-white). This custom listbox gives full control: dark
   popup, consistent hover, keyboard nav (↑/↓/Enter/Esc), focus trap.

   API preserved: children are option elements; `onChange` receives a
   synthetic `{ target: { value } }` event so existing consumers work
   without changes. */
const WRAP = 'relative inline-flex items-center bg-surface-raised border border-border-strong rounded-md focus-within:border-accent-blue focus-within:ring-1 focus-within:ring-accent-blue/30 transition-colors';
const TRIGGER = 'w-full flex items-center justify-between text-left bg-transparent text-text border-0 pl-[0.65rem] pr-[1.9rem] py-[0.4rem] font-[inherit] text-[0.85rem] cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed rounded-md';
const CARET = 'absolute right-[0.5rem] top-1/2 -translate-y-1/2 pointer-events-none text-text-muted text-[0.7rem]';
const POPUP = 'z-[150] max-w-[calc(100vw-1rem)] overflow-y-auto overflow-x-hidden bg-surface-el border border-border-strong rounded-md shadow-lg py-1 max-h-[280px] outline-none';
const ITEM_BASE = 'flex items-center px-[0.75rem] py-[0.4rem] text-[0.85rem] cursor-pointer select-none';

/* Extract option elements from children (supports both option nodes and
   React fragments of options). Each becomes { value, label, disabled }. */
function extractOptions(children) {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!child || typeof child !== 'object') return;
    if (child.type === React.Fragment) {
      out.push(...extractOptions(child.props.children));
      return;
    }
    if (child.type === 'option' || (child.props && 'value' in child.props)) {
      out.push({
        value: child.props.value,
        label: child.props.children,
        disabled: !!child.props.disabled,
      });
    }
  });
  return out;
}

export default function Select({
  value, onChange, children,
  className = '',
  compact = false,
  disabled = false,
  'aria-label': ariaLabel,
  title,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const listRef = useRef([]);

  const items = extractOptions(children);
  const selectedIndex = items.findIndex(i => String(i.value) === String(value ?? ''));
  const selected = items[selectedIndex];

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight, availableWidth }) {
          const safeAvailableWidth = Number.isFinite(availableWidth)
            ? Math.max(0, availableWidth - 8)
            : rects.reference.width;
          const safeWidth = Math.min(rects.reference.width, safeAvailableWidth);
          Object.assign(elements.floating.style, {
            minWidth: `${safeWidth}px`,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: `${Math.min(280, availableHeight - 8)}px`,
            overflowX: 'hidden',
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onNavigate: setActiveIndex,
    loop: true,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click, dismiss, role, listNav,
  ]);

  const handleSelect = (idx) => {
    const item = items[idx];
    if (!item || item.disabled) return;
    onChange?.({ target: { value: item.value } });
    setActiveIndex(null);
    setOpen(false);
  };

  const triggerCls = `${TRIGGER} ${compact ? '!py-[0.25rem] text-[0.8rem]' : ''}`;

  return (
    <>
      <span className={`${WRAP} ${className}`}>
        <button
          ref={refs.setReference}
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          title={title}
          {...getReferenceProps()}
          className={triggerCls}
        >
          <span className="truncate">{selected?.label ?? <em className="text-text-dim not-italic opacity-60">Select…</em>}</span>
        </button>
        <span aria-hidden className={CARET}>▾</span>
      </span>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className={POPUP}
            >
              {items.length === 0 && (
                <div className="px-[0.75rem] py-2 text-[0.82rem] text-text-dim italic">No options</div>
              )}
              {items.map((item, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = idx === selectedIndex;
                const tone = item.disabled
                  ? 'text-text-dim cursor-not-allowed'
                  : isSelected
                    ? 'text-accent-gold'
                    : 'text-text';
                const bg = item.disabled
                  ? ''
                  : isActive
                    ? 'bg-surface-hover'
                    : 'hover:bg-surface-hover';
                return (
                  <div
                    key={`${item.value}-${idx}`}
                    ref={(el) => { listRef.current[idx] = el; }}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={item.disabled || undefined}
                    tabIndex={isActive ? 0 : -1}
                    {...getItemProps({
                      onClick: () => handleSelect(idx),
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(idx);
                        }
                      },
                    })}
                    className={`${ITEM_BASE} ${tone} ${bg}`}
                  >
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {isSelected && <span className="text-accent-gold ml-2" aria-hidden>✓</span>}
                  </div>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
